/**
 * assetImageMapping.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

import { getProductImage } from './imageMapping';

const assetIdCache = new Map();

function extractAssetId(imageValue) {
  if (!imageValue) return null;
  const value = String(imageValue).trim();
  if (/^\d+$/.test(value)) return value;
  const match = value.match(/\/api\/assets\/(\d+)\/(?:download|thumbnail|view)/);
  if (match) return match[1];
  // formatProduct sometimes rewrites to /assets/.../Name_3812.jpg even when that file is missing
  const staticId = value.split('?')[0].match(/\/assets\/.+_(\d{3,})\.[a-z0-9]+$/i);
  return staticId ? staticId[1] : null;
}

function isDirectStaticImagePath(imageValue) {
  const value = String(imageValue || '');
  return (
    value.startsWith('/assets/')
    || value.startsWith('/uploads/')
    || value.startsWith('/images/')
  );
}

function isFilenameAssetReference(imageValue) {
  return Boolean(
    imageValue && (
      imageValue.match(/^\d{13}-[a-z0-9]+-/)
      || imageValue.match(/^file-\d+-[a-z0-9]+\./)
      || imageValue.match(/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/)
      || imageValue.includes('uploads/')
    )
  );
}

function stableVersion(product) {
  return product?.updated_at || product?.id || '';
}

function buildThumbnailUrl(assetId, product) {
  const version = stableVersion(product);
  return version
    ? `/api/assets/${assetId}/thumbnail?v=${encodeURIComponent(version)}`
    : `/api/assets/${assetId}/thumbnail`;
}

export const getProductImageUrl = async (product) => {
  if (!product) {
    return '/images/placeholder.jpg';
  }

  if (product.image) {
    const assetId = extractAssetId(product.image);
    if (assetId) {
      return buildThumbnailUrl(assetId, product);
    }

    if (isDirectStaticImagePath(product.image)) {
      return product.image;
    }

    if (isFilenameAssetReference(product.image)) {
      if (assetIdCache.has(product.image)) {
        const cachedId = assetIdCache.get(product.image);
        return buildThumbnailUrl(cachedId, product);
      }

      try {
        const response = await fetch(`/api/assets?search=${encodeURIComponent(product.image)}&file_type=image&limit=1`);
        if (response.ok) {
          const data = await response.json();
          const assets = data.assets || [];
          const matchingAsset = assets.find((asset) => asset.filename === product.image);

          if (matchingAsset) {
            assetIdCache.set(product.image, matchingAsset.id);
            return buildThumbnailUrl(matchingAsset.id, {
              ...product,
              updated_at: matchingAsset.updated_at || product.updated_at,
            });
          }
        }
      } catch (error) {
        console.error('Error searching for asset by filename:', error);
      }
    }

    return getProductImage(product);
  }

  return getProductImage(product);
};

export const getProductImageUrlSync = (product) => {
  if (!product) {
    return '/images/placeholder.jpg';
  }

  if (product.image) {
    const assetId = extractAssetId(product.image);
    if (assetId) {
      return buildThumbnailUrl(assetId, product);
    }

    if (isDirectStaticImagePath(product.image)) {
      return product.image;
    }

    if (isFilenameAssetReference(product.image)) {
      if (assetIdCache.has(product.image)) {
        const cachedId = assetIdCache.get(product.image);
        return buildThumbnailUrl(cachedId, product);
      }
      return '/images/placeholder.jpg';
    }

    return getProductImage(product);
  }

  return getProductImage(product);
};

export const productImageNeedsAsyncLookup = (product) => {
  if (!product?.image) return false;
  if (extractAssetId(product.image)) return false;
  if (isDirectStaticImagePath(product.image)) return false;
  return isFilenameAssetReference(product.image);
};

export const clearAssetIdCache = () => {
  assetIdCache.clear();
};
